<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import {
  useZuwadAssistantStore,
  ACTIVE_STATUSES,
} from 'dashboard/stores/zuwadAssistant';
import { useMapGetter } from 'dashboard/composables/store';
import AssistantMessage from './AssistantMessage.vue';
import AssistantComposer from './AssistantComposer.vue';

defineProps({
  compact: { type: Boolean, default: false },
});

const { t, te } = useI18n();
const store = useZuwadAssistantStore();
const scroller = ref(null);
const composer = ref(null);
const notice = ref('');

const currentUser = useMapGetter('getCurrentUser');
const firstName = computed(
  () => (currentUser.value?.name || '').trim().split(/\s+/)[0] || ''
);
const messages = computed(() => store.activeMessages);
const suggestions = computed(() => [
  t('ZUWAD_ASSISTANT.SUGGESTIONS.1'),
  t('ZUWAD_ASSISTANT.SUGGESTIONS.2'),
  t('ZUWAD_ASSISTANT.SUGGESTIONS.3'),
  t('ZUWAD_ASSISTANT.SUGGESTIONS.4'),
]);
const running = computed(() =>
  messages.value.find(
    message =>
      message.role === 'assistant' && ACTIVE_STATUSES.includes(message.status)
  )
);

// Follow new text only while the reader is already at the bottom.
const nearBottom = () => {
  const el = scroller.value;
  return !el || el.scrollHeight - el.scrollTop - el.clientHeight < 120;
};
const scrollDown = () =>
  nextTick(() => {
    if (scroller.value) scroller.value.scrollTop = scroller.value.scrollHeight;
  });
watch(
  () => [messages.value.length, messages.value.at(-1)?.content?.length],
  () => {
    if (nearBottom()) scrollDown();
  }
);
watch(() => store.activeThreadId, scrollDown);

// While this chat is on screen its answers count as seen: no badge, no push to the phone.
const onVisibility = () => {
  if (document.visibilityState === 'visible' && store.activeThreadId)
    store.markSeen(store.activeThreadId);
};
watch(
  () => store.activeThreadId,
  id => store.setVisibleThread(id || null),
  { immediate: true }
);
onMounted(() => document.addEventListener('visibilitychange', onVisibility));
onBeforeUnmount(() => {
  document.removeEventListener('visibilitychange', onVisibility);
  store.setVisibleThread(null);
});

const errorFor = code => {
  const key = `ZUWAD_ASSISTANT.ERRORS.${code}`;
  return te(key) ? t(key) : t('ZUWAD_ASSISTANT.ERRORS.failed');
};
const send = async ({ content, files }) => {
  notice.value = '';
  try {
    await store.send(content, files);
    scrollDown();
  } catch {
    notice.value = errorFor(store.error);
  }
};
// The asker, the chat's owner or a supervisor may stop a running answer.
const canStop = computed(
  () =>
    !!running.value &&
    (!running.value.user ||
      running.value.user.id === store.me ||
      !!store.activeThread?.can_manage)
);
const stop = () => canStop.value && store.stop(running.value).catch(() => {});
const approve = (message, choice) =>
  store.approve(message, choice).catch(() => {
    notice.value = errorFor('unreachable');
  });
</script>

<template>
  <div class="flex flex-col h-full min-h-0">
    <div
      ref="scroller"
      class="flex-1 min-h-0 overflow-y-auto px-4 py-5 space-y-6"
    >
      <div
        v-if="!messages.length"
        class="flex flex-col items-center justify-center h-full text-center gap-3 px-2"
      >
        <span
          class="flex items-center justify-center rounded-2xl size-14 bg-n-brand text-n-gold shadow-md ring-4 ring-n-brand/10"
        >
          <span class="i-woot-zuwad-ai size-8" />
        </span>
        <h3 class="mt-1 text-xl font-semibold text-n-slate-12">
          {{
            firstName
              ? t('ZUWAD_ASSISTANT.GREETING', { name: firstName })
              : t('ZUWAD_ASSISTANT.EMPTY_TITLE')
          }}
        </h3>
        <p class="max-w-md text-sm text-n-slate-11">
          {{ t('ZUWAD_ASSISTANT.EMPTY_HINT') }}
        </p>
        <div
          class="grid w-full max-w-xl gap-2 mt-2"
          :class="compact ? 'grid-cols-1' : 'sm:grid-cols-2'"
        >
          <button
            v-for="suggestion in suggestions"
            :key="suggestion"
            type="button"
            dir="auto"
            class="flex items-start gap-2 px-3.5 py-3 text-sm text-start rounded-xl border border-n-weak bg-n-solid-1 text-n-slate-12 transition-colors hover:border-n-brand/40 hover:bg-n-blue-2 disabled:opacity-50"
            :disabled="!store.configured"
            @click="composer?.fill(suggestion)"
          >
            <span
              class="i-lucide-corner-down-left size-4 mt-0.5 shrink-0 text-n-blue-11 rtl:-scale-x-100"
            />
            <span>{{ suggestion }}</span>
          </button>
        </div>
      </div>
      <AssistantMessage
        v-for="message in messages"
        :key="message.id"
        :message="message"
        @approve="approve(message, 'once')"
        @deny="approve(message, 'deny')"
      />
    </div>
    <p
      v-if="!store.configured"
      class="px-4 py-2 text-xs text-n-amber-11 bg-n-amber-2"
    >
      {{ t('ZUWAD_ASSISTANT.NOT_CONFIGURED') }}
    </p>
    <p v-if="notice" class="px-4 py-2 text-xs text-n-ruby-11 bg-n-ruby-2">
      {{ notice }}
    </p>
    <AssistantComposer
      ref="composer"
      :compact="compact"
      :busy="!!running || store.sending"
      :can-stop="canStop"
      :disabled="!store.configured"
      @send="send"
      @stop="stop"
      @rejected="notice = errorFor('image_invalid')"
    />
  </div>
</template>
